/**
 * Time-range preset resolver.
 *
 * Presets are stored in the URL as `tr.preset` and resolve to a concrete
 * `{ st, ed }` window at render time. This keeps shared rolling links
 * ("Last 24 hours") honoring "now" instead of freezing the timestamp at
 * the moment the link was generated.
 */

export type TimeRangePreset =
	'last-15m' | 'last-1h' | 'last-24h' | 'last-7d' | 'last-30d'

export type TimeRangeSpec =
	{ preset: TimeRangePreset } | { st: string; ed: string }

export type ResolvedTimeRange = { st: string; ed: string }

const PRESET_DURATION_MS: Record<TimeRangePreset, number> = {
	'last-15m': 15 * 60 * 1000,
	'last-1h': 60 * 60 * 1000,
	'last-24h': 24 * 60 * 60 * 1000,
	'last-7d': 7 * 24 * 60 * 60 * 1000,
	'last-30d': 30 * 24 * 60 * 60 * 1000,
}

export function isPresetRange(
	range: TimeRangeSpec | undefined,
): range is { preset: TimeRangePreset } {
	return !!range && 'preset' in range
}

export function isAbsoluteRange(
	range: TimeRangeSpec | undefined,
): range is { st: string; ed: string } {
	return !!range && 'st' in range && 'ed' in range
}

/**
 * Resolve a preset or absolute range against `now`.
 * @param now Defaults to the current time. Pass a fixed Date in tests.
 */
export function resolveTimeRange(
	range: TimeRangeSpec | undefined,
	now: Date = new Date(),
): ResolvedTimeRange | undefined {
	if (!range) return undefined

	if (isPresetRange(range)) {
		const durationMs = PRESET_DURATION_MS[range.preset]
		const ed = now.toISOString()
		const st = new Date(now.getTime() - durationMs).toISOString()
		return { st, ed }
	}

	if (isAbsoluteRange(range)) {
		return { st: range.st, ed: range.ed }
	}

	return undefined
}
